import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { TranslateLoader, TranslateModule, TranslationObject } from '@ngx-translate/core';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import {
  provideKeycloak,
  withAutoRefreshToken,
  AutoRefreshTokenService,
  UserActivityService,
  includeBearerTokenInterceptor,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
} from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { environment } from '../environments/environment';
import {
  HttpClient,
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { Observable } from 'rxjs';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import { ekowitrynaPreset, provideEkoHostAppMetadata } from '@ekowitryna/ui';
import packageJson from '../../package.json';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  Configuration as KobizeConfiguration,
  provideApi
} from '@mobiletract/kobize-service-angular-client';
import { provideApi as provideAuthApi, Configuration as AuthConfiguration } from '@mobiletract/auth-service-angular-client';
import { BASE_PATH } from '@mobiletract/account-service-angular-client';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import {
  EkoLayoutAppBarComponent,
  EkoLayoutSidebarComponent,
  EkoNavigationMenuComponent,
  EkoScrollToTopComponent,
} from '@ekowitryna/ui';
import { CompanyContextPanelComponent } from './features/company-context/company-context-panel.component';
import { FirstConfigurationComponent } from './features/first-configuration/first-configuration.component';
import { CompanyDataRequiredComponent } from './shared/components/company-data-required/company-data-required.component';
import { ProfileRedirectComponent } from './profile-redirect.component';
import { provideEkoPreferencesSync } from './core/providers/eko-dark-mode.providers';
import { provideEkoBrowserTitle } from '@ekowitryna/ui';
import { GroupCacheInterceptor } from './shared/interceptors/group-cache.interceptor';

const KOBIZE_API_BASE_PATH = `${environment.apiUrl}/kobize`;

const API_URL_PATTERN = new RegExp(
  `^${environment.apiUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`
);

export function createTranslateLoader(http: HttpClient): TranslateLoader {
  return {
    getTranslation: (lang: string): Observable<TranslationObject> =>
      http.get<TranslationObject>(`./assets/i18n/${lang}.json`)
  };
}

@NgModule({
  declarations: [
    AppComponent,
    CompanyContextPanelComponent,
    FirstConfigurationComponent,
    CompanyDataRequiredComponent,
    ProfileRedirectComponent
  ],
  imports: [
    BrowserModule,
    CommonModule,
    TranslateModule.forRoot({
      defaultLanguage: 'pl',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    }),
    AppRoutingModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    SelectModule,
    SkeletonModule,
    TooltipModule,
    EkoLayoutAppBarComponent,
    EkoLayoutSidebarComponent,
    EkoNavigationMenuComponent,
    EkoScrollToTopComponent
  ],
  bootstrap: [AppComponent],
  providers: [
    provideKeycloak({
      config: {
        url: environment.keycloakUrl,
        realm: environment.realm,
        clientId: environment.clientId
      },
      initOptions: {
        onLoad: 'check-sso',
        checkLoginIframe: false,
        silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html'
      },
      features: [
        withAutoRefreshToken({ onInactivityTimeout: 'none' })
      ],
      providers: [AutoRefreshTokenService, UserActivityService]
    }),
    provideHttpClient(
      withInterceptors([includeBearerTokenInterceptor]),
      withInterceptorsFromDi()
    ),
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [{ urlPattern: API_URL_PATTERN }]
    },
    MessageService,
    provideEkoHostAppMetadata({
      name: packageJson.name,
      version: packageJson.version
    }),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: ekowitrynaPreset,
        options: {
          darkModeSelector: '.dark',
          cssLayer: false
        }
      }
    }),
    provideApi(KOBIZE_API_BASE_PATH),
    {
      provide: KobizeConfiguration,
      useFactory: (keycloak: Keycloak) =>
        new KobizeConfiguration({
          basePath: KOBIZE_API_BASE_PATH,
          credentials: {
            'bearer-jwt': () => keycloak.token ?? ''
          }
        }),
      deps: [Keycloak]
    },
    { provide: BASE_PATH, useValue: environment.accountsApiUrl },
    provideAuthApi(environment.authApiUrl),
    {
      provide: AuthConfiguration,
      useFactory: (keycloak: Keycloak) =>
        new AuthConfiguration({
          basePath: environment.authApiUrl,
          credentials: {
            'Bearer JWT Token': () => keycloak.token ?? '',
          },
        }),
      deps: [Keycloak],
    },
    GroupCacheInterceptor,
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: GroupCacheInterceptor,
      multi: true,
    },
    ...provideEkoPreferencesSync(),
    provideEkoBrowserTitle('app.browserTitle'),
  ]
})
export class AppModule { }
